import React, { useMemo } from "react";
import { CardContent } from "@/components/ui/card";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import DashboardCardHeader from "@/components/common/DashboardCardHeader";
import { Typography } from "@/components/ui/typography";
import HealthPlanIcon from "@/assets/icons/healthplan.svg";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";
import { formatDate } from "@/constants/utilityFunctions";

interface HealthPlanData {
  memberHealthPlan: string;
  memberStatus: string;
  enrollmentStartDate: string;
  enrollmentEndDate: string;
  eligibilityLastChecked: string;
}

const handleNullValues = (
  data: HealthPlanData | null
): HealthPlanData | null => {
  if (!data || typeof data !== "object") return data;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processed: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") {
      processed[key] = "-";
    } else {
      processed[key] = value;
    }
  }
  return processed as HealthPlanData;
};

interface HealthPlanCardProps {
  shouldBlink?: boolean;
}

const HealthPlanCard: React.FC<HealthPlanCardProps> = ({ shouldBlink = false }) => {
  const {
    cardVariant,
    isFavourite,
    toggleFavourite,
  } = useDashboardCardFavorite("HEALTH_PLAN");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const { data: healthplan, loading, error, isErrorRed } = useFetchCard<HealthPlanData>({
    endpoint: routes.cards.healthplan,
    memberId,
    processData: (rawData: unknown) => handleNullValues((rawData as { data: HealthPlanData }).data),
  });


  const display = useMemo(() => {
    if (!healthplan) return null;

    return {
      lastUpdated: formatDate(healthplan.eligibilityLastChecked),
      fields: {
        "Health Plan": healthplan.memberHealthPlan,
        "Enrollment Start Date": formatDate(healthplan.enrollmentStartDate),
        "Enrollment End Date": formatDate(healthplan.enrollmentEndDate),
        "Member Status": healthplan.memberStatus,
      } as Record<string, string>,
    };
  }, [healthplan]);

  const lastUpdatedText = (() => {
    if (loading) return "Loading...";
    if (error) return "-";
    return display?.lastUpdated || "-";
  })();

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <img src={HealthPlanIcon} alt='Healthplan' className='w-3 h-4' />
            </BorderedIconWrapper>
          }
          className='h-[56px]'
          title={
            <div className='flex flex-col'>
              <Typography
                variant='semibold-16'
                className='text-grey-800 dark:text-white'>
                Health Plan
              </Typography>
              <Typography
                className='text-grey-600 dark:text-grey-500 text-right'
                style={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: "12px",
                  lineHeight: "20px",
                  letterSpacing: "0px",
                }}>
                Last updated on{" "}
                {lastUpdatedText}
              </Typography>
            </div>
          }
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 lg:h-[210px] lg:mb-[21px] md:mb-[29px]'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={6} showProfile={false} />;
            }
            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[170px] md:h-[130px]'>
                  <Typography
                    variant='medium-14'
                    className={`${isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                      } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }
            if (!healthplan) {
              return null;
            }
            return (
              <>
              {/* Data Fields */}
              <div className='gap-[16px] flex flex-col'>
                {display &&
                  Object.entries(display.fields).map(([key, value]) => (
                    <div key={key} className='flex items-center gap-3'>
                      <div className='flex-1 flex justify-between items-center'>
                        <Typography
                          variant='medium-14'
                          className='text-grey-700 dark:text-grey-500'>
                          {key}
                        </Typography>
                        {key === "Member Status" &&
                          (value === "Active" || value === "Inactive") ? (
                          <Badge
                            variant={value === "Active" ? "success" : "warning"}
                            size='sm'
                            className='h-[24px] min-w-[53px] flex items-center justify-center'>
                            {value}
                          </Badge>
                        ) : (
                          <Typography
                            variant='medium-14'
                            className='text-gray-800 dark:text-white text-right'>
                            {value}
                          </Typography>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
              </>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default HealthPlanCard;
